/**
 * Computes the minimal change (a single replaced range) that turns `oldDoc`
 * into `newDoc` by trimming the shared prefix and suffix. Dispatching this
 * instead of replacing the whole document from position 0 keeps CodeMirror's
 * contentEditable caret in sync with the editor state: a full `from: 0`
 * replacement rebuilds the content DOM and can leave the native caret behind
 * an interior space, causing the next keystroke to land at the wrong position.
 *
 * @internal
 */
export function computeMinimalDocChange(
  oldDoc: string,
  newDoc: string,
): { from: number; to: number; insert: string } {
  const maxCommon = Math.min(oldDoc.length, newDoc.length);

  let prefix = 0;
  while (prefix < maxCommon && oldDoc[prefix] === newDoc[prefix]) {
    prefix++;
  }

  let suffix = 0;
  while (
    suffix < maxCommon - prefix &&
    oldDoc[oldDoc.length - 1 - suffix] === newDoc[newDoc.length - 1 - suffix]
  ) {
    suffix++;
  }

  return {
    from: prefix,
    to: oldDoc.length - suffix,
    insert: newDoc.slice(prefix, newDoc.length - suffix),
  };
}
